import { Card } from "@/components/ui/card"
import { Clock, GripVertical } from "lucide-react"
import { intervalToDuration } from "date-fns"
import { formatDuration } from "@/shared/utils/date-fn"
import type { WeeklyObjective } from "../../entities/weekly-objectives/weekly-objectives"
import type { SyntheticListenerMap } from "@dnd-kit/core/dist/hooks/utilities"
import type { DraggableAttributes } from "@dnd-kit/core"
import { Checkbox } from "@/components/ui/checkbox"
import { msToSeconds } from "../../utils/time"
import { cn } from "@/lib/utils"
import useCoursesInfo from "@/modules/courses/hooks/use-courses-info"
import { useProductTour } from "@/modules/product-tour/stores/use-product-tour"

export interface WeeklyObjectivesCardProps extends WeeklyObjective {
  dragListeners?: SyntheticListenerMap
  dragAttributes?: DraggableAttributes
  displayObjective?: () => void
  isPending?: boolean
  canHideCard?: boolean
  isAnOldColumn?: boolean
}

export default function WeeklyObjectivesCard({
  name,
  status,
  weeklyObjectiveIndex,
  estimatedDuration,
  approvedActivities,
  totalActivities,
  subjectId,
  dragListeners,
  dragAttributes,
  displayObjective,
  isPending,
  canHideCard,
  estimatedRemainingDuration,
  isAnOldColumn,
}: WeeklyObjectivesCardProps) {
  const { getCourseColor } = useCoursesInfo()
  const color = getCourseColor(subjectId)
  const isApproved = status === "approved"
  const totalSeconds = msToSeconds(estimatedDuration)
  const totalRemainingSeconds = msToSeconds(estimatedRemainingDuration)
  const objectiveProgress = (approvedActivities / totalActivities) * 100
  const { isActive } = useProductTour()
  const duration = intervalToDuration({
    start: 0,
    end: isAnOldColumn ? totalSeconds * 1000 : totalRemainingSeconds * 1000,
  })
  const formattedDuration = formatDuration(duration).min()

  function onDisplayObjective() {
    if (isActive) {
      return
    }
    displayObjective?.()
  }

  return (
    <Card
      className={cn(
        "relative w-full select-none overflow-hidden hover:cursor-pointer hover:bg-neutral-100",
        canHideCard && "bg-transparent opacity-0",
        isPending && "pointer-events-none opacity-60",
        isActive && "hover:cursor-default",
      )}
      onClick={onDisplayObjective}
    >
      <div
        className={"absolute h-full opacity-15"}
        style={{ backgroundColor: color, width: `${objectiveProgress}%` }}
      />
      <div className="absolute h-full w-1" style={{ backgroundColor: color }} />
      <div className="px-4 py-2">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 space-y-1">
            <div className="flex justify-between">
              <p className="text-sm font-normal">Objetivo {weeklyObjectiveIndex}</p>
              <div className="relative left-2 flex items-center gap-1">
                <div className="flex items-center gap-1">
                  {isApproved ? (
                    <Checkbox checked />
                  ) : (
                    <Clock className="h-3 w-3 text-neutral-600" />
                  )}

                  <p className="text-xs text-neutral-600">{isApproved ? "" : formattedDuration}</p>
                </div>
                <div
                  {...dragAttributes}
                  {...dragListeners}
                  className={cn(isActive && "cursor-default")}
                >
                  <GripVertical
                    style={{ display: isApproved ? "none" : "flex" }}
                    className="h-4 w-4 text-neutral-400"
                  />
                </div>
              </div>
            </div>
            <p className={cn("text-sm font-normal text-neutral-600", isApproved && "line-through")}>
              {name}
            </p>
          </div>
        </div>
      </div>
    </Card>
  )
}
